'use client';

import Link from 'next/link';
import { Timer, Gavel, FileCheck } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { Button } from '@/components/ui/button/button';
import { Heading } from '@/components/ui/heading/heading';
import { fadeSlideUp, scaleIn, staggerContainer } from '@/utils/animation/variants';
import type { TCity } from '@/shared/types';

type TCityCtaProps = {
  city: TCity;
  className?: string;
  /** 'band' = full-width promo card (default); 'card' = compact sidebar version. */
  variant?: 'band' | 'card';
};

const chips = [
  { icon: Timer, label: 'Czas realizacji', value: '6–12 tygodni' },
  { icon: Gavel, label: 'Aukcje', value: 'Copart / IAAI' },
  { icon: FileCheck, label: 'Formalności', value: 'pełna odprawa' },
];

/**
 * Conversion CTA into the existing funnel (FR-005): calculator (primary) + contact.
 * Cost lives in the calculator — no per-city price shown here (FR-002/FR-011).
 */
export const CityCta = ({ city, className = '', variant = 'band' }: TCityCtaProps) => {
  const prefersReducedMotion = useReducedMotion();
  const isCard = variant === 'card';

  return (
    <m.div
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={staggerContainer}
      className={`rounded-[28px] border border-neutral-900/40 bg-neutral-900/30 shadow-[0_15px_45px_rgba(0,0,0,0.35)] ${
        isCard ? 'p-6' : 'px-6 py-10 md:px-12 md:py-12'
      } ${className}`}
    >
      <m.div variants={fadeSlideUp} className="flex flex-col gap-2">
        <span className="inline-flex w-fit items-center rounded-full border border-action/40 bg-action/10 px-3 py-1 text-xs font-oswald uppercase tracking-[0.2em] text-action">
          Zacznij import
        </span>
        <Heading variant="h2" highlight="USA" className={isCard ? 'text-2xl leading-tight text-white' : 'text-3xl leading-tight text-white'}>
          {`Sprowadź auto z USA do ${city.nameGenitive}`}
        </Heading>
        <p className="text-sm leading-relaxed text-neutral-300">
          Policz orientacyjny koszt importu w kalkulatorze lub skontaktuj się z nami po indywidualną wycenę.
        </p>
      </m.div>

      {!isCard && (
        <m.div variants={staggerContainer} className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {chips.map((chip) => (
            <m.div
              key={chip.label}
              variants={scaleIn}
              className="flex items-center gap-3 rounded-2xl border border-neutral-800 bg-backgroundLight/80 p-4"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-900 text-action">
                <chip.icon className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-neutral-400">{chip.label}</p>
                <p className="font-oswald text-white">{chip.value}</p>
              </div>
            </m.div>
          ))}
        </m.div>
      )}

      <m.div variants={fadeSlideUp} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button as={Link} href="/kalkulator" variant="primary" className="px-6">
          Przejdź do kalkulatora
        </Button>
        <Button as={Link} href="/kontakt" variant="secondary" className="px-6">
          Skontaktuj się z nami
        </Button>
      </m.div>
    </m.div>
  );
};
